import React, { Fragment, memo, useReducer, useRef, useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { createUseStyles, Input, Modal } from '@Sage/hoi-poi-ui';

import {
    downloadTemplate,
    exportEntity,
    getFieldsSchema,
    getIdsToExport,
    getExportStatus,
} from 'lib/services/imports';
import { downloadURI } from '/lib/helpers';
import { useWeb5Context } from 'lib/Web5Context';
import { validateEmail } from 'lib/helpers';
import { showErrorToast } from 'lib/errors';
import { MAX_EXPORT_ROWS } from 'lib/constants';
import { TYPES } from '../constants';
import { isEqual } from 'utils/objects';
import { successToast } from 'utils/toast';
import { formatDate } from 'utils/time';

import ModalOptions from 'components/ModalOptions';
import ModalFilters from 'components/ModalFilters';
import ProgressContent from 'components/ProgressContent';

import styles from './styles';

const useStyles = createUseStyles(styles, 'ExportModal');

const initialState = {
    fieldsSchema: [],
    filtersSchema: [],
    entity: null,
    importType: null,
    email: undefined,
    validEmail: true,
    isOpen: false,
    isLoading: false,
    optionsIsReady: false,
    filtersIsReady: false,
    step: 1,
    disabled: {},
    selected: {},
    numSelected: 0,
    numMandatory: 0,
    filters: {},
    selectOptions: [],
    selectedFilters: [],
    openFilters: {},
    filtersValues: {},
    ids: [],
    isDirect: true,
    count: null,
    url: null,
    progress: 0,
};

function reducer(state, action) {
    switch (action.type) {
        case 'init':
            return {
                ...initialState,
                isOpen: true,
            };
        case 'onOptionsReady':
            return { ...state, optionsIsReady: true, ...action.data };
        case 'onFiltersReady':
            return { ...state, filtersIsReady: true, ...action.data };
        case 'onClose':
            return { ...state, isOpen: false };
        case 'changeEmail':
            return { ...state, email: action.email, validEmail: true };
        case 'setInvalidEmail':
            return { ...state, validEmail: false };
        case 'setLoading':
            return { ...state, isLoading: action.value };
        case 'setStep':
            return { ...state, step: action.value };
        case 'setSelected':
            return { ...state, selected: action.selected, numSelected: action.numSelected };
        case 'setDownloadUrl':
            return { ...state, url: action.value };
        case 'setProgress':
            return { ...state, progress: action.value };
        case 'onChangeSelectedFilters':
            state = { ...state, ...action.data };
            return state;
        case 'onToggleFilter':
            state = {
                ...state,
                openFilters: {
                    ...state.openFilters,
                    ...action.data,
                },
            };
            return state;
        case 'onChangeFilter':
            state = {
                ...state,
                isLoading: true,
                filtersValues: {
                    ...state.filtersValues,
                    ...action.data,
                },
            };
            return state;
        case 'onChangeFilters':
            return { ...state, ...action.data, isLoading: false };
        default:
            throw new Error('No action provided');
    }
}

const getDateFilterIds = (filtersSchema = []) => {
    return filtersSchema.reduce((ids, section) => {
        const dateFields = (section?.fields || [])
            .filter((field) => field?.dataType === 'date')
            .map((field) => field.id);

        return ids.concat(dateFields);
    }, []);
};

const normalizeDateRange = (value = []) => {
    if (!Array.isArray(value) || value.length !== 2) return value;

    const [from, to] = value;
    if (!to) return [from, to];

    const toDate = to instanceof Date ? new Date(to.getTime()) : new Date(to);
    if (Number.isNaN(toDate.getTime())) return [from, to];

    toDate.setDate(toDate.getDate() + 1);
    toDate.setSeconds(toDate.getSeconds() - 1);

    // Format dates with time (YYYY-MM-DDTHH:mm:ss) instead of Date objects
    // to avoid UTC conversion and ensure proper date range handling
    const fromStr = from
        ? formatDate({ dateString: from, format: 'YYYY-MM-DDTHH:mm:ss', UTC: false })
        : '';
    const toStr = formatDate({ dateString: toDate, format: 'YYYY-MM-DDTHH:mm:ss', UTC: false });

    return [fromStr, toStr];
};

const formatValues = (filters, dateFilterIds = []) => {
    const finalObj = Object.keys(filters).reduce((obj, key) => {
        let value = filters[key];
        if (value === undefined || value === null || value === '') return obj;
        if (Array.isArray(value)) {
            value = value.map((el) => {
                if (
                    typeof el === 'object' &&
                    !Array.isArray(el) &&
                    el !== null &&
                    el.hasOwnProperty('value')
                ) {
                    return el.value;
                } else return el;
            });
            if (dateFilterIds.includes(key)) {
                value = normalizeDateRange(value);
            }
        } else if (typeof value === 'object' && 'value' in value) {
            value = value.value;
        }
        return { ...obj, [key]: value };
    }, {});

    return finalObj;
};

const removeEmpty = (obj) =>
    Object.fromEntries(
        Object.entries(obj).filter((value) => value !== null && value !== undefined),
    );

const ExportModal = ({
    onRef,
    entityLogName,
    maxImportColumns,
    maxImportRows,
    shrinkedTab,
    matchBy,
}) => {
    const classes = useStyles();
    const { t, logEvent, email } = useWeb5Context();
    const [state, dispatch] = useReducer(reducer, initialState);

    const timeout = useRef(null);
    const numTries = useRef(0);
    const lapse = useRef(1000);
    const exportCountRequestVersion = useRef(0);

    const beginExportCountRequest = useCallback(() => {
        exportCountRequestVersion.current += 1;
        return exportCountRequestVersion.current;
    }, []);

    const invalidateExportCountRequests = useCallback(() => {
        exportCountRequestVersion.current += 1;
    }, []);

    const isLatestExportCountRequest = useCallback(
        (requestVersion) => requestVersion === exportCountRequestVersion.current,
        [],
    );

    // Child method
    useEffect(() => {
        onRef &&
            onRef({
                open(entity, importType, withData, syncNames) {
                    invalidateExportCountRequests();
                    dispatch({ type: 'init' });
                    const entityName = shrinkedTab ? shrinkedTab?.value : entity;
                    getFieldsSchema(entityName)
                        .then(({ data }) => {
                            const standardOptions = data.standard.map((el) => ({
                                ...el,
                                id: el.name,
                                label:
                                    importType && el.mandatory
                                        ? `${el.description_field}*`
                                        : el.description_field,
                                comment: syncNames?.includes(el.name)
                                    ? `(${t('cfm_label_import_identifier')})`
                                    : undefined,
                            }));
                            const extraOptions = data.extra.map((el) => ({
                                ...el,
                                id: el.name,
                                label:
                                    importType && el.mandatory
                                        ? `${el.description_field}*`
                                        : el.description_field,
                                comment: syncNames?.includes(el.name)
                                    ? t('cfm_label_import_identifier')
                                    : undefined,
                            }));
                            let standardMandatory = [];
                            let extraMandatory = [];
                            if (importType === 'create') {
                                standardMandatory = standardOptions
                                    .filter((el) => el.mandatory)
                                    .map((el) => el.id);
                                extraMandatory = extraOptions
                                    .filter((el) => el.mandatory)
                                    .map((el) => el.id);
                            } else if (importType === 'update') {
                                standardMandatory = standardOptions
                                    .filter((el) => syncNames.includes(el.id))
                                    .map((el) => el.id);
                                extraMandatory = extraOptions
                                    .filter((el) => syncNames.includes(el.id))
                                    .map((el) => el.id);
                            }
                            const selected = {
                                standard: {
                                    isSelected: false,
                                    options: [...standardMandatory],
                                },
                                extra: {
                                    isSelected: false,
                                    options: [...extraMandatory],
                                },
                            };
                            const disabled = {
                                standard: {
                                    options: [...standardMandatory],
                                },
                                extra: {
                                    options: [...extraMandatory],
                                },
                            };
                            const numSelected = importType
                                ? standardMandatory.length + extraMandatory.length
                                : 0;
                            const numMandatory = standardMandatory.length + extraMandatory.length;
                            const maxRowsToExport = importType ? maxImportRows : MAX_EXPORT_ROWS;
                            let filtersSchema = [];
                            let selectOptions = [];

                            const fieldsSchema = [
                                {
                                    label: t('cfm_label_standard'),
                                    id: 'standard',
                                    options: standardOptions,
                                },
                                {
                                    label: t('cfm_label_extra_fields'),
                                    id: 'extra',
                                    options: extraOptions,
                                },
                            ];

                            if (withData) {
                                const mapOptions = (options) =>
                                    options
                                        .map((el) => ({ label: el.label, value: el.id }))
                                        .sort((a, b) => a.label.localeCompare(b.label));

                                const standardFilters = data.standard
                                    .map((el) => {
                                        return {
                                            label: el.description_field,
                                            id: el.name,
                                            fields: [
                                                {
                                                    id: el.name,
                                                    dataType: TYPES[el.type],
                                                    inputProps: {
                                                        placeholder: t('cfm_placeholder_search'),
                                                        options:
                                                            el.type === 'bit'
                                                                ? [
                                                                      {
                                                                          label: t('cfm_label_yes'),
                                                                          value: true,
                                                                      },
                                                                      {
                                                                          label: t('cfm_label_no'),
                                                                          value: false,
                                                                      },
                                                                  ]
                                                                : [],
                                                    },
                                                    inputAttrs: {
                                                        list: el.tablaVinculada,
                                                        isFuzzy: el.fuzzySearchValueList,
                                                    },
                                                },
                                            ],
                                        };
                                    })
                                    .filter((el) => el.id !== 'id' && el.id !== 'ExtID');

                                const extraFilters = data.extra.map((el) => {
                                    return {
                                        label: el.description_field,
                                        id: el.name,
                                        fields: [
                                            {
                                                id: el.name,
                                                dataType: TYPES[el.type],
                                                inputProps: {
                                                    placeholder: t('cfm_placeholder_search'),
                                                },
                                                inputAttrs: {
                                                    list: el.tablaVinculada,
                                                    isFuzzy: el.fuzzySearchValueList,
                                                },
                                            },
                                        ],
                                    };
                                });

                                filtersSchema = [...standardFilters, ...extraFilters];

                                selectOptions = [
                                    {
                                        label: t('cfm_label_standard'),
                                        id: 'standard',
                                        options: mapOptions(standardFilters),
                                    },
                                    {
                                        label: t('cfm_label_extra_fields'),
                                        id: 'extra',
                                        options: mapOptions(extraFilters),
                                    },
                                ];
                            }

                            dispatch({
                                type: 'onOptionsReady',
                                data: {
                                    entity: entityName,
                                    importType,
                                    withData,
                                    email,
                                    selected,
                                    disabled,
                                    numSelected,
                                    numMandatory,
                                    selectOptions,
                                    fieldsSchema,
                                    filtersSchema,
                                    maxRowsToExport,
                                },
                            });
                        })
                        .catch((err) => {
                            console.error(err);
                        });
                },
            });
    }, [onRef, dispatch, email, t, maxImportRows, shrinkedTab, invalidateExportCountRequests]);

    useEffect(() => {
        return () => {
            clearTimeout(timeout.current);
        };
    }, []);

    // Modal handlers
    const onClose = useCallback(() => {
        invalidateExportCountRequests();
        dispatch({ type: 'onClose' });
    }, [dispatch, invalidateExportCountRequests]);

    const startCheckBulkStatus = useCallback(
        (id, url) => {
            timeout.current = setTimeout(() => {
                getExportStatus(id)
                    .then(({ data }) => {
                        if (!state.isOpen) return;
                        if (data.status === 3) {
                            downloadURI(url);
                            logEvent({
                                event: entityLogName,
                                submodule: state.entity,
                                functionality: 'export',
                            });
                            onClose();
                        } else {
                            numTries.current++;
                            if (numTries.current >= 10) {
                                lapse.current = 1000;
                            }
                            if (state.progress !== data.progress) {
                                dispatch({ type: 'setProgress', value: data.progress });
                            }
                            startCheckBulkStatus(id, url);
                        }
                    })
                    .catch((error) => {
                        console.error(error);
                        timeout.current && clearTimeout(timeout.current);
                    });
            }, lapse.current);
        },
        [entityLogName, logEvent, onClose, state.entity, state.isOpen, state.progress],
    );

    const onChangeFilter = useCallback(
        ({ filter: field, value }) => {
            const data = field ? { [field.id]: value } : {};
            const filters = { ...state.filtersValues, ...data };

            dispatch({ type: 'onChangeFilter', data });

            const dateFilterIds = getDateFilterIds(state.filtersSchema);
            const activeFilters = formatValues(filters, dateFilterIds);
            const params = { ...activeFilters, fieldsCount: state.numSelected };
            const emptyFilters = Object.values(activeFilters).reduce(
                (acc, value) => acc && Array.isArray(value) && !value.length,
                true,
            );

            if (emptyFilters) {
                invalidateExportCountRequests();
                const data = { count: null, ids: [], isDirect: true };

                dispatch({ type: 'onChangeFilters', data });
            } else {
                const requestVersion = beginExportCountRequest();
                getIdsToExport(state.entity, params)
                    .then(({ data }) => {
                        if (!isLatestExportCountRequest(requestVersion)) return;
                        if (!data) throw new Error('No data');
                        dispatch({ type: 'onChangeFilters', data });
                    })
                    .catch((err) => {
                        if (!isLatestExportCountRequest(requestVersion)) return;
                        dispatch({ type: 'setLoading', value: false });
                        console.warn(err);
                    });
            }
        },
        [
            beginExportCountRequest,
            invalidateExportCountRequests,
            isLatestExportCountRequest,
            state.entity,
            state.filtersSchema,
            state.filtersValues,
            state.numSelected,
        ],
    );

    const onConfirm = useCallback(() => {
        if (state.withData && state.step === 1) {
            if (!state.filtersIsReady) {
                getIdsToExport(state.entity, { fieldsCount: state.numSelected })
                    .then(({ data }) => {
                        dispatch({
                            type: 'onFiltersReady',
                            data: data?.count < state.maxRowsToExport ? data : {},
                        });
                    })
                    .catch((err) => {
                        dispatch({ type: 'setLoading', value: false });
                        console.warn(err);
                    });
            }
            return dispatch({ type: 'setStep', value: 2 });
        }
        if (!state.isDirect && !validateEmail(state.email))
            return dispatch({ type: 'setInvalidEmail' });

        dispatch({ type: 'setLoading', value: true });
        const fields = [...state.selected.standard.options, ...state.selected.extra.options];
        if (state.withData) {
            const data = {
                entity: state.entity,
                fields,
                ids: state.ids,
                email: state.isDirect ? undefined : state.email,
            };

            if (matchBy) {
                data.searchType = parseInt(matchBy, 10);
            }

            exportEntity(data)
                .then(({ data }) => {
                    if (!data) showErrorToast(t, {}, 'error_unexpected');
                    else if (state.isDirect) {
                        dispatch({ type: 'setDownloadUrl', value: data.url });
                        startCheckBulkStatus(data.idExport, data.url);
                    } else {
                        logEvent({
                            event: entityLogName,
                            submodule: state.entity,
                            functionality: 'exportToMail',
                        });
                        successToast({
                            t,
                            title: t('label_success'),
                            text: t('label_toast_export_email_incoming'),
                        });
                        onClose();
                    }
                })
                .catch((error) => {
                    showErrorToast(t, error, 'cfm_error_delete_values_list');
                    onClose();
                });
        } else {
            downloadTemplate(state.entity, fields)
                .then(({ data }) => {
                    if (data?.url) {
                        return downloadURI(data.url);
                    } else {
                        showErrorToast(t, {}, 'error_unexpected');
                    }
                })
                .then(() => {
                    successToast({
                        t,
                        title: t('cfm_toast_export_in_progress_success'),
                        text: t('cfm_toast_export_in_progress_success_desc'),
                    });
                    onClose();
                })
                .catch((error) => {
                    showErrorToast(t, error);
                    dispatch({ type: 'setLoading', value: false });
                });
        }
    }, [
        entityLogName,
        logEvent,
        onClose,
        startCheckBulkStatus,
        state.email,
        state.entity,
        state.filtersIsReady,
        state.ids,
        state.isDirect,
        state.maxRowsToExport,
        state.numSelected,
        state.selected,
        state.step,
        state.withData,
        matchBy,
        t,
    ]);

    const onChangeSelected = useCallback((data) => {
        const numSelected = data.standard.options.length + data.extra.options.length;

        dispatch({
            type: 'setSelected',
            selected: data,
            numSelected,
        });
    }, []);

    const onBack = useCallback((e) => dispatch({ type: 'setStep', value: 1 }), []);

    const onChangeEmail = useCallback(
        (value) =>
            dispatch({
                type: 'changeEmail',
                email: value || '',
            }),
        [],
    );

    const onChangeSelectedFilters = useCallback(
        (selectedFilters) => {
            const filtersValues = selectedFilters.reduce((obj, el) => {
                return {
                    ...obj,
                    [el.value]: state.filtersValues[el.value],
                };
            }, {});
            const openFilters = selectedFilters.reduce((obj, el) => {
                return {
                    ...obj,
                    [el.value]:
                        state.openFilters[el.value] === false ? state.openFilters[el.value] : true,
                };
            }, {});

            const dateFilterIds = getDateFilterIds(state.filtersSchema);
            const params = formatValues(filtersValues, dateFilterIds);
            const comparableParams = formatValues(filtersValues);
            const comparableFilterParams = formatValues(state.filtersValues);
            const withValues = Object.values(params).filter((value) => {
                return value !== null && value !== undefined;
            });
            const cleanFiltersValues = removeEmpty(comparableParams);
            const cleanStateValues = removeEmpty(comparableFilterParams);
            if (!withValues.length) {
                invalidateExportCountRequests();
                const data = { count: null, ids: [], isDirect: true };
                dispatch({ type: 'onChangeFilters', data });
            } else if (withValues.length && !isEqual(cleanFiltersValues, cleanStateValues)) {
                dispatch({ type: 'setLoading', value: true });
                const requestVersion = beginExportCountRequest();
                getIdsToExport(state.entity, { ...params, fieldsCount: state.numSelected })
                    .then(({ data }) => {
                        if (!isLatestExportCountRequest(requestVersion)) return;
                        if (!data) throw new Error('No data');
                        dispatch({ type: 'setLoading', value: false });
                        dispatch({ type: 'onChangeFilters', data });
                    })
                    .catch((err) => {
                        if (!isLatestExportCountRequest(requestVersion)) return;
                        dispatch({ type: 'setLoading', value: false });
                        console.warn(err);
                    });
            }
            dispatch({
                type: 'onChangeSelectedFilters',
                data: { selectedFilters, filtersValues, openFilters },
            });
        },
        [
            beginExportCountRequest,
            invalidateExportCountRequests,
            isLatestExportCountRequest,
            state.entity,
            state.filtersSchema,
            state.filtersValues,
            state.numSelected,
            state.openFilters,
        ],
    );

    const onToggleFilter = useCallback(
        ({ filter }) => {
            const isToggled = state.openFilters[filter] ? false : true;

            dispatch({ type: 'onToggleFilter', data: { [filter]: isToggled } });
        },
        [state.openFilters],
    );

    const postComponent = useMemo(() => {
        if (state.isDirect || state.count >= state.maxRowsToExport) return null;
        return (
            <Input
                placeholder={t('cfm_placeholder_type_email')}
                onChange={onChangeEmail}
                value={state.email}
                isFullWidth
                label={t('cfm_label_text_choose_export_email')}
                labelMode="vertical"
                error={state.validEmail ? undefined : t('label_invalid_email')}
                type="email"
            />
        );
    }, [
        state.isDirect,
        state.count,
        state.maxRowsToExport,
        state.email,
        state.validEmail,
        t,
        onChangeEmail,
    ]);

    const title = useMemo(() => {
        if (state.importType === 'create' && !state.withData)
            return t('cfm_label_download_empty_template_creation_import');
        if (state.importType === 'update' && !state.withData)
            return t('cfm_label_download_empty_template_update_import');
        if (state.importType === 'create' && state.withData)
            return t('cfm_label_download_data_template_creation_import', [state.step]);
        if (state.importType === 'update' && state.withData)
            return t('cfm_label_download_data_template_update_import', [state.step]);
        return t('cfm_label_export_excel', [state.step]);
    }, [state.importType, state.step, state.withData, t]);

    // Modal Options props
    const modalOptionsProps = useMemo(() => {
        const getAdvice = () => {
            if (state.importType && state.numSelected > maxImportColumns)
                return t('cfm_label_download_template_too_many_fields', [maxImportColumns]);
            if (
                (state.importType === 'create' && state.numSelected > state.numMandatory) ||
                (state.importType === 'update' && state.numSelected > state.numMandatory) ||
                (!state.importType && state.numSelected > 0)
            )
                return t('cfm_label_num_fields_exported', [state.numSelected]);
            if (state.importType === 'create')
                return t('cfm_label_download_template_creation_import_desc');
            if (state.importType === 'update')
                return t('cfm_label_download_template_update_import_desc');
            return t('label_export_at_least_one_field');
        };
        const overrides = {
            confirmButton: {
                isLoading: state.isLoading,
                isDisabled:
                    !state.numSelected ||
                    (state.importType === 'update' && state.numSelected === state.numMandatory) ||
                    (state.importType && state.numSelected > maxImportColumns),
            },
        };

        return {
            schema: state.fieldsSchema,
            title,
            className: classes.root,
            isOpen: state.isOpen && state.step === 1,
            adviceType:
                state.numSelected === 0 || state.numSelected > maxImportColumns
                    ? 'warning'
                    : 'info',
            advice: getAdvice(),
            onCancel: onClose,
            onRequestClose: onClose,
            onConfirm,
            onChangeSelected,
            cancelText: t('cfm_action_cancel'),
            confirmText: state.withData ? t('cfm_action_continue') : t('action_export_excel'),
            placeholderSearch: t('label_search_field'),
            overrides,
            disabled: state.disabled,
            isLoading: !state.optionsIsReady,
        };
    }, [
        state.isLoading,
        state.numSelected,
        state.importType,
        state.numMandatory,
        state.fieldsSchema,
        state.isOpen,
        state.step,
        state.withData,
        state.disabled,
        state.optionsIsReady,
        maxImportColumns,
        title,
        classes.root,
        onClose,
        onConfirm,
        onChangeSelected,
        t,
    ]);

    // Modal Filters props
    const modalFiltersProps = useMemo(() => {
        const getAdvice = () => {
            if (state.count >= state.maxRowsToExport)
                return t('cfm_label_download_template_too_many_registers', [state.maxRowsToExport]);
            if (!state.isDirect) return t('label_export_switched_to_email');
            if (state.count !== null) return t('cfm_label_num_registers_exported', [state.count]);
            return t('cfm_label_download_template_import_2_desc');
        };

        const confirmIsDisabled =
            !state.count ||
            state.count >= state.maxRowsToExport ||
            (!state.isDirect && (!state.email || !state.validEmail));

        return {
            schema: state.filtersSchema,
            title,
            className: classes.root,
            isOpen: state.isOpen && state.step === 2,
            isReady: state.filtersIsReady,
            adviceType: state.numSelected > maxImportColumns ? 'warning' : 'info',
            advice: getAdvice(),
            onCancel: onClose,
            onConfirm,
            onBack,
            onRequestClose: onClose,
            confirmIsDisabled,
            confirmText: t(state.isDirect ? 'action_export_excel' : 'action_send_email'),
            cancelText: t('cfm_action_cancel'),
            backText: t('cfm_action_back_previous_step'),
            placeholderSearch: t('cfm_placeholder_search'),
            disabled: state.disabled,
            onChangeEmail,
            postComponent,
            selectOptions: state.selectOptions,
            selectedFilters: state.selectedFilters,
            openFilters: state.openFilters,
            filtersValues: state.filtersValues,
            onChangeSelectedFilters,
            onChangeFilter,
            onToggleFilter,
        };
    }, [
        state.count,
        state.maxRowsToExport,
        state.isDirect,
        state.email,
        state.validEmail,
        state.filtersSchema,
        state.isOpen,
        state.step,
        state.filtersIsReady,
        state.numSelected,
        state.disabled,
        state.selectOptions,
        state.selectedFilters,
        state.openFilters,
        state.filtersValues,
        title,
        classes.root,
        maxImportColumns,
        onClose,
        onConfirm,
        onBack,
        t,
        onChangeEmail,
        postComponent,
        onChangeSelectedFilters,
        onChangeFilter,
        onToggleFilter,
    ]);

    if (state.url)
        return (
            <Modal
                title={t('title_export_loading_modal')}
                isOpen={state.isOpen}
                onCancel={onClose}
                cancelText={t('cfm_action_cancel')}
            >
                <ProgressContent
                    description={t('label_bulk_loading_description')}
                    subDescription={t('label_bulk_loading_description_2')}
                    current={state.progress}
                    total={100}
                    withRemaining={false}
                />
            </Modal>
        );
    return (
        <Fragment>
            <ModalOptions {...modalOptionsProps} selected={state.selected} />
            <ModalFilters
                {...modalFiltersProps}
                values={state.filters}
                isLoading={state.isLoading}
            />
        </Fragment>
    );
};

ExportModal.propTypes = {
    onRef: PropTypes.func,
};

export default memo(ExportModal);
